/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Device, DeviceInfo } from '@capacitor/device';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { Platform } from '@ionic/angular';
import { MobileRegistrationRequest } from 'src/app/models/mobile-registration';
import { environment } from 'src/environments/environment';
import { Browser } from '@capacitor/browser';
import {
  ActionPerformed as LocalNotificationActionPerformed,
  LocalNotifications,
} from '@capacitor/local-notifications';
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings,
} from 'capacitor-native-settings';
import { BehaviorSubject, Subject } from 'rxjs';
import { DeviceService } from '../device/device.service';
import { localStorageKeys } from '../localStorage/localStorageVariables';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { Badge } from '@capawesome/capacitor-badge';
import { AccountService } from '../user/account.service';
import { NotificationAnalyticsService } from '../analytics/notification-analytics.service';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
// const { PushNotifications } = Plugins;

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  public hasNotification= new Subject<any>();
  isPermissionGranted = false;
  notificationSettings = new BehaviorSubject<boolean | undefined>(undefined);
  isNotificationEnabled$ = this.notificationSettings.asObservable();
  hasLatestNotification = this.hasNotification.asObservable();

  constructor(
    private router: Router,
    private deviceService: DeviceService,
    private platform: Platform,
    private localStorageService: LocalStorageService,
    private accountService: AccountService,
    private notificationAnalyticsService: NotificationAnalyticsService
  ) {}

  async initPush() {
    if (Capacitor.getPlatform() !== 'web') {
      this.registerNotification();
    } else {
      // eslint-disable-next-line max-len
      // TODO: Disabled web push notifications for now, since there is no clarity on how it will work for clients who use this app as a pwa inside the webview of other native app.
      //   this.webNotificationService.requestPermission().subscribe(
      //     async (token) => {
      //       if (!token) {
      //         //TODO Add analytics for permission not granted
      //       } else {
      //         // this.registerDevice(this.serializeWebToMobileRegistrationRequest(token));
      //       }
      //     },
      //     async (err) => {
      //       //TODO Add analytics for permission not granted
      //     }
      //   );
      //   this.webNotificationService.getMessages().subscribe(async (msg: any) => {
      //     const alert = await this.alertCtrl.create({
      //       header: msg.notification.title,
      //       subHeader: msg.notification.body,
      //       message: msg.data.info,
      //       cssClass:'generic-alert',
      //       buttons: ['OK'],
      //     });
      //     await alert.present();
      //   });
    }
  }

  addListners() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    PushNotifications.addListener('registration', async (token: Token) => {
      if (token.value && !this.deviceService.deviceID) {
        this.localStorageService.setItem(
          localStorageKeys.MOBILE_REGISTRATION_TOKEN,
          token.value
        );
        if (
          this.notificationSettings.getValue() &&
          this.accountService.isLoggedIn()
        ) {
          this.registerDevice(token.value);
        }
      }
      // alert('Push registration success, token: ' + token.value);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      //TODO Analytics of failed registration
      // alert('Error on registration: ' + JSON.stringify(error));
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        // IMPORTANT: Firebase "Messaging campaigns" open metrics are only attributed for
        // notifications opened from the system tray. If we re-display the message as a
        // LocalNotification while the app is foregrounded, Firebase cannot attribute
        // the open to the campaign (you'll typically see notification_receive but not opens).
        //
        // For non-campaign pushes, we still show a local notification on Android.
        if (Capacitor.getPlatform() !== 'ios') {
          const data = notification?.data;
          const isFirebaseCampaign =
            !!data?.['google.c.a.c_id'] ||
            !!data?.['google.c.a.c_l'] ||
            !!data?.['google.message_id'] ||
            !!data?.['gcm.message_id'] ||
            !!data?.['gcm.n.analytics_data'];
          if (!isFirebaseCampaign) {
            this.sendLocalNotification(notification);
          } else {
            console.info('[push] Firebase campaign message received in foreground; skipping LocalNotification to preserve open attribution (test by backgrounding app).');
          }
        }
        if(notification && (notification?.data?.type === 'webview')){
          this.hasNotification.next(true);
        }

        this.notificationAnalyticsService.trackNotificationReceived(notification);
        // eslint-disable-next-line max-len
        // Add notification to local storage against user so that he should be able to see the notification history on the page when the page is implemented
        // eslint-disable-next-line max-len
        // May be post notification data to the APi to store the notification recieved data at user level which can be fetched accross the user dervices when needed .
        // the notfication pane must be developed in such a way that it should be accessible from the menu in the app in the last options
        // Need to implement the badge for notificaiton permission if not given by the user,
        // User should be able to get the notification permission promp on demand
      }
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        // this.removeAllDeliveredNotifications();
        const data = notification?.notification?.data;
        console.info('[push] actionPerformed payload keys:', data ? Object.keys(data) : []);
        await this.logFirebaseComposerOpen(data, notification?.notification?.id);

        if (notification.actionId === 'tap') {
          this.notificationAnalyticsService.trackNotificationOpened(notification);
        } else {
          this.notificationAnalyticsService.trackNotificationActionClicked(notification);
        }

        this.handleNotification(notification);
      }
    );

    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      async (notification: LocalNotificationActionPerformed) => {
        // this.removeAllDeliveredNotifications();
        const data = notification?.notification?.extra;
        console.info('[local] actionPerformed payload keys:', data ? Object.keys(data) : []);
        await this.logFirebaseComposerOpen(data, notification?.notification?.id?.toString());
        this.handleNotification(notification);
      }
    );
  }

  checkNotificationStatus(isregistrationNeeded = false) {
    PushNotifications.checkPermissions().then(async (response) => {
      if (response.receive !== 'granted') {
        this.notificationSettings.next(false);
      } else {
        if (isregistrationNeeded) {
          if (
            this.accountService.isLoggedIn() &&
            !this.accountService.deviceID &&
            Capacitor.getPlatform() !== 'web'
          ) {
            if (!this.notificationSettings.getValue()) {
              await PushNotifications.register();
              this.notificationSettings.next(true);
            }
          } else {
            this.notificationSettings.next(true);
          }
        } else {
          this.notificationSettings.next(true);
        }
      }
    });
  }

  async getDeviceInfo() {
    return await Device.getInfo();
  }

  async openNotificationSetting() {
    if (Capacitor.getPlatform() === 'android') {
      NativeSettings.openAndroid({
        option: AndroidSettings.AppNotification,
      }).then((response) => {});
    }
    if (Capacitor.getPlatform() === 'ios') {
      NativeSettings.openIOS({
        option: IOSSettings.App,
      }).then((response) => {});
    }
  }
  async clearBadge() {
    try {
      await Badge.clear();
    } catch (error) {
      console.error('Error clearing badge:', error);
    }
  }

  removeAllDeliveredNotifications() {
    if (Capacitor.getPlatform() !== 'web') {
      PushNotifications.checkPermissions().then((response) => {
        if (response.receive === 'granted') {
          PushNotifications.removeAllDeliveredNotifications();
        }
      });
    }
  }

  async registerDevice(token) {
    const info: DeviceInfo = await Device.getInfo();
    const deviceId = await Device.getId();
    const deviceInfo = {
      width: this.platform.width(),
      height: this.platform.height(),
      uuid: deviceId,
      ...info,
    };
    const mobileRegistrationRequest = this.serializeToMobileRegistrationRequest(
      deviceInfo,
      token
    );
    this.deviceService.platformID =
      mobileRegistrationRequest.platformid.identifier;

    if (this.notificationSettings.getValue()) {
      this.deviceService
        .mobileRegistration(mobileRegistrationRequest)
        .subscribe({
          next: (response) => {
            this.deviceService.deviceID = response.device_id;
          },
          error: (err: any) => {},
          complete: () => {},
        });
    }
  }

  public async handleNotification(notification) {
    const data =
      notification?.notification?.data || notification?.notification?.extra || {};

    const target = data?.type_id || data?.deep_link || data?.link;
    if (!target) {
      return;
    }

    const isAbsoluteUrl = /^https?:\/\//i.test(target);

    if (data.type === 'webview') {
      // If Firebase sends a full URL, open it directly.
      // If Firebase sends an app route/path, navigate inside Ionic.
      if (isAbsoluteUrl) {
        await Browser.open({ url: target });
      } else {
        const route = target.startsWith('/') ? target : `/${target}`;
        this.router.navigateByUrl(route);
      }
      return;
    }

    if (isAbsoluteUrl) {
      await Browser.open({ url: target });
    } else {
      const route = target.startsWith('/') ? target : `/${target}`;
      this.router.navigateByUrl(route);
    }
  }

  private registerNotification() {
    PushNotifications.checkPermissions().then(async (response) => {
      if (response.receive === 'denied') {
        // this.isPermissionGranted = false;
        this.notificationSettings.next(false);
        this.notificationAnalyticsService.trackPermissionDenied('pre_existing');
        // Enable a UI state that should prompt the user to enable push notifications again
      } else if (response.receive === 'granted') {
        // this.isPermissionGranted = true;
        if (!this.notificationSettings.getValue()) {
          await PushNotifications.register();
          this.notificationSettings.next(true);
          //check if register is been already called
        }
        // check if register is already called if not then call register
        // await PushNotifications.register();
      } else {
        PushNotifications.requestPermissions().then(async (result) => {
          if (result.receive === 'granted') {
            this.notificationAnalyticsService.trackPermissionGranted();
            if (!this.notificationSettings.getValue()) {
              //check if register is been already called
              await PushNotifications.register();
              this.notificationSettings.next(true);
            }

            // this.isPermissionGranted = true;
          } else {
            this.notificationSettings.next(false);
            this.notificationAnalyticsService.trackPermissionDenied('prompt');
            // this.isPermissionGranted = false;
            // Enable a UI state that should prompt the user to enable push notifications again
          }
        });
      }
    });
  }

  private serializeToMobileRegistrationRequest(deviceInfo: any, token: string) {
    return {
      key: environment.key,
      pid: environment.pid,
      rid: token,
      deviceid: deviceInfo.model,
      platformid: deviceInfo.uuid,
      uuid: deviceInfo.uuid,
      w: deviceInfo.width,
      h: deviceInfo.height,
      osv: deviceInfo.osVersion,
      sdkv: '',
      appv: deviceInfo.webViewVersion,
      appvc: 0,
      platform: deviceInfo.platform,
      ga_id: deviceInfo.uuid, // replace with google advertise identifier,
      utm_campaign: 'default',
      utm_medium: 'default',
      utm_source: 'default',
      mobileno: '',
      ucity: '',
      ucountry: '',
    } as MobileRegistrationRequest;
  }
  private sendLocalNotification(notification) {
    const id = this.toLocalNotificationId(notification?.id);
    const imageUrl = notification?.data?.imageUrl;

    LocalNotifications.schedule({
      notifications: [
        {
          title: notification?.title || '',
          body: notification?.body || '',
          id,
          ...(imageUrl
            ? {
                attachments: [
                  {
                    id: 'site',
                    url: imageUrl,
                  },
                ],
              }
            : {}),
          actionTypeId: '',
          extra: notification?.data || {},
        },
      ],
    });
  }


  /**
   * Manual notification open logging kept for production.
   *
   * The Firebase Campaigns screen should still primarily use the native Firebase
   * FCM/Analytics attribution from the original system notification tap. These
   * manual events are kept as an app-side fallback and for DebugView verification.
   */
  private async logFirebaseComposerOpen(data: any, fallbackNotificationId?: string) {
    try {
      const analyticsData = this.parseFirebaseAnalyticsData(
        data?.['gcm.n.analytics_data']
      );

      const messageId =
        data?.['google.message_id'] ||
        data?.['gcm.message_id'] ||
        data?.['message_id'] ||
        analyticsData?.['google.c.a.e'] ||
        fallbackNotificationId ||
        'unknown';

      const campaignId =
        data?.['google.c.a.c_id'] ||
        data?.['campaign_id'] ||
        analyticsData?.['google.c.a.c_id'];

      const campaignLabel =
        data?.['google.c.a.c_l'] ||
        data?.['campaign_label'] ||
        analyticsData?.['google.c.a.c_l'];

      const messageLabel =
        data?.['google.c.a.m_l'] ||
        data?.['message_label'] ||
        analyticsData?.['google.c.a.m_l'];

      await FirebaseAnalytics.logEvent({
        name: 'tx_notification_open',
        params: {
          notification_id: fallbackNotificationId || messageId,
          message_id: messageId,
          campaign_id: campaignId || 'unknown',
          campaign_label: campaignLabel || 'unknown',
          message_label: messageLabel || 'unknown',
        },
      });

      await FirebaseAnalytics.logEvent({
        name: 'notification_open',
        params: {
          message_id: messageId,
          message_name: campaignLabel || messageLabel || 'unknown',
          message_device_time: Date.now(),
          message_time: Date.now(),
          message_type: data?.['message_type'] || 'fcm',
          ...(campaignId ? { campaign_id: campaignId } : {}),
        },
      });
    } catch (error) {
      console.warn('logFirebaseComposerOpen failed', error);
    }
  }

  private parseFirebaseAnalyticsData(rawAnalyticsData: any): any {
    if (!rawAnalyticsData || typeof rawAnalyticsData !== 'string') {
      return {};
    }

    try {
      return JSON.parse(rawAnalyticsData);
    } catch (error) {
      return {};
    }
  }

  private toLocalNotificationId(rawId: any): number {
    const asNumber = typeof rawId === 'number' ? rawId : Number(rawId);
    if (Number.isFinite(asNumber) && asNumber > 0) return Math.floor(asNumber);
    // Keep within 32-bit signed int range for Android
    return Math.floor(Date.now() % 2147483647);
  }

}
